import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Search, Calendar, User, Clock, Filter } from 'lucide-react';
import api from '@/utils/api';

const BRAND = { navy: '#0B1120', gold: '#C9A84C' };

const ACTION_COLORS = {
  CREATE_BOOKING: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  RECORD_PAYMENT: 'bg-blue-100 text-blue-800 border-blue-200',
  CREATE_CUSTOMER: 'bg-purple-100 text-purple-800 border-purple-200',
  UPDATE_BOOKING: 'bg-amber-100 text-amber-800 border-amber-200',
  DELETE: 'bg-red-100 text-red-800 border-red-200',
};

const ACTION_LABELS = {
  CREATE_BOOKING: 'New Booking',
  RECORD_PAYMENT: 'Payment Recorded',
  CREATE_CUSTOMER: 'New Customer',
  UPDATE_BOOKING: 'Booking Updated',
};

export default function ActivityLogs({ user, onLogout }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let url = '/activity-logs?limit=200';
      if (dateFilter) url += `&date=${dateFilter}`;
      if (roleFilter && roleFilter !== 'all') url += `&user_role=${roleFilter}`;
      const res = await api.get(url);
      setLogs(res.data || []);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, [dateFilter, roleFilter]);

  const filtered = logs.filter(log => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (log.details || '').toLowerCase().includes(s) ||
           (log.user_name || '').toLowerCase().includes(s) ||
           (log.action || '').toLowerCase().includes(s);
  });

  const formatTime = (ts) => {
    if (!ts) return '';
    try {
      const d = new Date(ts);
      return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
    } catch { return ts; }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-4" data-testid="activity-logs-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold" style={{ color: BRAND.navy }}>Activity Logs</h1>
            <p className="text-sm text-slate-500">Billing & admin activity audit trail</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-8 h-9 w-48 text-sm"
                data-testid="log-search"
              />
            </div>
            <Input
              type="date"
              value={dateFilter}
              onChange={e => setDateFilter(e.target.value)}
              className="h-9 w-40 text-sm"
              data-testid="log-date-filter"
            />
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="h-9 w-32 text-sm" data-testid="log-role-filter">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="billing">Billing</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="sales">Sales</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={fetchLogs} className="h-9 gap-1">
              <Filter size={14} /> Refresh
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="border-emerald-200 bg-emerald-50/50">
            <CardContent className="p-3 text-center">
              <p className="text-lg font-bold text-emerald-700">{filtered.filter(l => l.action === 'CREATE_BOOKING').length}</p>
              <p className="text-xs text-emerald-600">Bookings Created</p>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-3 text-center">
              <p className="text-lg font-bold text-blue-700">{filtered.filter(l => l.action === 'RECORD_PAYMENT').length}</p>
              <p className="text-xs text-blue-600">Payments Recorded</p>
            </CardContent>
          </Card>
          <Card className="border-purple-200 bg-purple-50/50">
            <CardContent className="p-3 text-center">
              <p className="text-lg font-bold text-purple-700">{filtered.filter(l => l.action === 'CREATE_CUSTOMER').length}</p>
              <p className="text-xs text-purple-600">Customers Created</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-3 text-center">
              <p className="text-lg font-bold text-slate-700">{filtered.length}</p>
              <p className="text-xs text-slate-500">Total Activities</p>
            </CardContent>
          </Card>
        </div>

        {/* Logs Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-400 text-sm">Loading logs...</div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm">No activity logs found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left px-4 py-2.5 font-medium text-slate-600 whitespace-nowrap"><Clock size={13} className="inline mr-1" />Timestamp</th>
                      <th className="text-left px-4 py-2.5 font-medium text-slate-600 whitespace-nowrap"><User size={13} className="inline mr-1" />User</th>
                      <th className="text-left px-4 py-2.5 font-medium text-slate-600">Role</th>
                      <th className="text-left px-4 py-2.5 font-medium text-slate-600">Action</th>
                      <th className="text-left px-4 py-2.5 font-medium text-slate-600"><FileText size={13} className="inline mr-1" />Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((log, i) => (
                      <tr key={log.id || i} className="border-b hover:bg-slate-50/50 transition-colors" data-testid={`log-row-${i}`}>
                        <td className="px-4 py-2.5 whitespace-nowrap text-xs text-slate-500">
                          <Calendar size={12} className="inline mr-1" />
                          {formatTime(log.timestamp)}
                        </td>
                        <td className="px-4 py-2.5 whitespace-nowrap font-medium text-slate-700">
                          {log.user_name || 'Unknown'}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${log.user_role === 'admin' ? 'bg-red-100 text-red-700' : log.user_role === 'billing' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}`}>
                            {log.user_role}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                            {ACTION_LABELS[log.action] || log.action}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-slate-600 max-w-md truncate" title={log.details}>
                          {log.details}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
